import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { partTypeTire } from "../../../constants/partTypes";
import { Image } from "../../../ui/Image/Image";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { PriceDisplay } from "../../../ui/PriceDisplay/PriceDisplay";
import { fixTireImageUrl } from "../../../util/fixTireImageUrl";
import { getPartFallbackSrc } from "../../../util/getPartFallbackSrc";
import { OrderItemRemoveButton } from "../CartPreview/OrderItemRemoveButton";
import { NormalizedDealerOrder } from "./useNormalizedDealerOrder";

type DealerOrderListProps = {
  data: NormalizedDealerOrder[];
  revalidate: VoidFunction;
};

export const DealerOrderList: FunctionComponent<DealerOrderListProps> = ({
  data,
  revalidate,
}) => {
  const { t } = useTranslation();

  return (
    <ul className="p-2">
      {data.map((item) => {
        return (
          <li
            className="md:flex md:items-center gap-2 md:gap-4 lg:gap-8"
            key={item.key}
          >
            <div className="flex gap-2 md:gap-4 mb-2 md:mb-0 md:items-center">
              <Image
                className={clsx(
                  "w-14 h-14 md:w-32 md:h-32 bg-input rounded-section object-cover object-center",
                  "text-transparent scale-x-[-1]"
                )}
                alt="Product Image"
                errorClassName="opacity-20 blur-[8px]"
                fallbackSrc={getPartFallbackSrc(partTypeTire)}
                src={fixTireImageUrl(item.imageLink)}
              />
              <div>
                <h3>{item.itemTitle}</h3>
                <KeyValue className="text-xs md:text-sm">
                  <>{t("dealerOrderModal.natPart")}:</>
                  <>{item.tireManufacturerPartNumber}</>
                </KeyValue>
                <KeyValue className="text-xs md:text-sm">
                  <>{t("dealerOrderModal.tireSize")}:</>
                  <>{item.formattedTiresize}</>
                </KeyValue>
                <KeyValue className="text-xs md:text-sm">
                  <>{t("dealerOrderModal.loadSpeedPly")}:</>
                  <>
                    {item.tireLoadRating}/{item.tireSpeedIndex}/
                    {item.tirePlyRating}
                  </>
                </KeyValue>
              </div>
            </div>
            <KeyValue
              className="md:flex-col md:items-start text-xs md:text-sm"
              valueClassName="md:text-base"
            >
              <>{t("dealerOrderModal.paymentQty")}:</>
              <>{item.quantity}</>
            </KeyValue>
            {/* <KeyValue
              className="md:flex-col md:items-start text-xs md:text-sm tw-highlight"
              valueClassName="md:text-base"
            >
              <>{t("dealerOrderModal.sourcedQty")}:</>
              <>{item.source}</>
            </KeyValue>
            <KeyValue
              className="md:flex-col md:items-start text-xs md:text-sm tw-highlight"
              valueClassName="md:text-base"
            >
              <>{t("dealerOrderModal.estDeliveryDate")}:</>
              <>{item.estDeliveryDate}</>
            </KeyValue> */}
            <KeyValue
              className="md:flex-col md:items-start text-xs md:text-sm"
              valueClassName="md:text-base"
            >
              <>{t("dealerOrderModal.unitCost")}:</>
              <PriceDisplay fallback={t("dealerOrderModal.nationalPricing")}>
                {item.cost}
              </PriceDisplay>
            </KeyValue>
            <KeyValue
              className="md:flex-col md:items-start text-xs md:text-sm"
              valueClassName="md:text-base"
            >
              <>{t("dealerOrderModal.total")}:</>
              <PriceDisplay fallback={t("dealerOrderModal.nationalPricing")}>
                {item.lineTotal}
              </PriceDisplay>
            </KeyValue>
            <KeyValue
              className="md:flex-col md:items-start text-xs md:text-sm"
              valueClassName="md:text-base"
            >
              <>{t("dealerOrderModal.delivery")}:</>
              <>{item.delivery}</>
            </KeyValue>
            <OrderItemRemoveButton
              className="md:ml-auto"
              onRemoved={revalidate}
              orderItem={item.orderItem}
            />
          </li>
        );
      })}
    </ul>
  );
};
